(() => {
  if (window.rxkImageIntroInstalled) return;
  window.rxkImageIntroInstalled = true;
  const previewLogo = root => {
    const reduced = matchMedia('(prefers-reduced-motion: reduce)');
    if (reduced.matches || navigator.connection?.saveData || ['slow-2g', '2g'].includes(navigator.connection?.effectiveType)) return;
    const controller = new AbortController();
    const animations = [];
    root.append(root.querySelector('[data-logo-intro-template]').content.cloneNode(true));
    const mark = root.querySelector('.rxk-logo-intro__mark');
    root.classList.add('rxk-image-intro--logo');
    let timer;
    const stop = () => {
      clearTimeout(timer); controller.abort(); root.hidden = true;
      animations.forEach(animation => animation.cancel()); mark.remove();
      root.classList.remove('rxk-image-intro--logo');
    };
    timer = setTimeout(stop, 1300);
    root.hidden = false;
    try {
      animations.push(mark.querySelector('.rxk-logo-intro__line').animate([
        { transform: 'scaleX(0)' }, { transform: 'scaleX(1)' }
      ], { duration: 480, delay: 100, fill: 'both', easing: 'cubic-bezier(.22,1,.36,1)' }));
      animations.push(mark.animate([{ opacity: 1 }, { opacity: 0 }], {
        duration: 180, delay: 660, fill: 'forwards', easing: 'ease-out'
      }));
      const exit = root.animate([{ opacity: 1 }, { opacity: 0 }], {
        duration: 180, delay: 840, fill: 'forwards', easing: 'ease-out'
      });
      animations.push(exit); exit.finished.then(stop).catch(() => {});
    } catch (_) { stop(); return; }
    ['pointerdown', 'wheel', 'keydown'].forEach(type => document.addEventListener(type, stop, { signal: controller.signal, capture: true, passive: true }));
    reduced.addEventListener('change', stop, { signal: controller.signal });
    document.addEventListener('visibilitychange', () => { if (document.hidden) stop(); }, { signal: controller.signal });
    window.addEventListener('pagehide', stop, { signal: controller.signal });
  };
  let gsapLoading;
  const loadGsap = url => {
    if (window.gsap) return Promise.resolve();
    if (!gsapLoading) gsapLoading = new Promise(resolve => {
      const script = document.createElement('script');
      script.src = url; script.async = true;
      script.onload = resolve;
      script.onerror = () => { gsapLoading = null; script.remove(); resolve(); };
      document.head.append(script);
    });
    return gsapLoading;
  };
  const initialize = root => {
    if (root.introInitialized) return;
    root.introInitialized = true;
    // Preview-only concept: never changes the configured image-stack mode.
    if (new URLSearchParams(location.search).get('image-preloader') === 'logo') { previewLogo(root); return; }
    const editor = Boolean(window.Shopify?.designMode);
    const forced = new URLSearchParams(location.search).has('image-preloader');
    const reduced = matchMedia('(prefers-reduced-motion: reduce)');
    const key = 'rxk-image-intro' + (root.dataset.frequency === 'page' ? ':' + location.pathname : '');
    let stop = () => {};
    const play = async () => {
      stop();
      if (reduced.matches || !root.querySelector('template').content.querySelector('img')) return;
      if (!editor && root.dataset.eligible !== 'true') return;
      let seen = false;
      try { seen = Boolean(sessionStorage.getItem(key)); } catch (_) {}
      if (!editor && !forced && seen && root.dataset.frequency !== 'always') return;
      document.querySelectorAll('.rxk-pl.ist-an').forEach(el => el.classList.remove('ist-an'));
      const controller = new AbortController();
      let timeline;
      const content = root.querySelector('template').content.cloneNode(true);
      root.append(content);
      const stack = root.querySelector('.rxk-image-intro__stack');
      let frames = [...stack.children];
      const duration = Math.min(6000, Math.max(2000, Number(root.dataset.duration) * 1000 || 3000));
      let timer;
      stop = () => {
        clearTimeout(timer);
        controller.abort();
        root.hidden = true;
        timeline?.kill();
        stack.querySelectorAll('video').forEach(video => video.pause());
        stack.remove();
      };
      // Independent safety timer: a failed media request must never trap visitors.
      timer = setTimeout(stop, duration + 1000);
      ['pointerdown', 'wheel', 'keydown'].forEach(type => document.addEventListener(type, stop, { signal: controller.signal, capture: true, passive: true }));
      document.addEventListener('visibilitychange', () => { if (document.hidden) stop(); }, { signal: controller.signal });
      reduced.addEventListener('change', stop, { signal: controller.signal });
      window.addEventListener('pagehide', stop, { signal: controller.signal });
      // Decode before animating; slow or failed media must not extend the intro.
      const decoded = new Set();
      let deadline;
      await Promise.race([
        Promise.all([loadGsap(root.dataset.gsap), ...frames.map(async frame => {
          try { await frame.querySelector('img').decode(); decoded.add(frame); } catch (_) {}
        })]),
        new Promise(resolve => { deadline = setTimeout(resolve, 700); })
      ]);
      clearTimeout(deadline);
      if (controller.signal.aborted) return;
      frames = frames.filter(frame => {
        if (decoded.has(frame)) return true;
        frame.remove(); return false;
      });
      if (!frames.length || !window.gsap) { stop(); return; }
      root.hidden = false;
      try { if (!editor) sessionStorage.setItem(key, '1'); } catch (_) {}
      try {
        const gsap = window.gsap;
        const end = Math.max(2.95, (frames.length - 1) * .36 + 1.15);
        timeline = gsap.timeline({ paused: true, onComplete: stop });
        gsap.set(stack, { scale: .60 });
        gsap.set(frames, { clipPath: 'inset(0 0 100% 0)' });
        frames.forEach((frame, index) => {
          timeline.to(frame, { clipPath: 'inset(0 0 0% 0)', duration: .65, ease: 'power2.inOut' }, index * .36);
          if (index > 0) timeline.set(frames[index - 1], { visibility: 'hidden' }, index * .36 + .65);
          const video = frame.querySelector('video');
          if (video && !navigator.connection?.saveData) {
            video.addEventListener('playing', () => { video.dataset.playing = ''; }, { signal: controller.signal });
            video.play().catch(() => {});
          }
        });
        // Same stack sequence as Motion Study, without its subsequent gallery spread.
        timeline.to(stack, { scale: .66, duration: 2.9, ease: 'sine.inOut' }, 0);
        timeline.set(stack, { scale: .66 }, end);
        timeline.timeScale(end / (duration / 1000)).play(0);
      } catch (_) { stop(); }
      ['pointerdown', 'wheel', 'keydown'].forEach(type => root.addEventListener(type, event => {
        event.preventDefault(); event.stopPropagation(); stop();
      }, { signal: controller.signal, passive: false }));
    };
    if (editor) {
      const section = root.closest('.shopify-section');
      section.addEventListener('shopify:section:select', play);
      section.addEventListener('shopify:block:select', play);
      section.addEventListener('shopify:section:deselect', () => stop());
      section.addEventListener('shopify:section:unload', () => stop());
    } else play();
  };
  const start = () => document.querySelectorAll('[data-image-intro]').forEach(initialize);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true }); else start();
  document.addEventListener('shopify:section:load', start);
})();
